import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/shadcn/table'
import { TableCard } from '@/components/common/table/TableCard'
import { PaginationFooter } from '@/components/common/table/PaginationFooter'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import { SortableTableHead } from '@/components/common/SortableTableHead'
import { FilterRow } from '@/components/common/filters/FilterRow'
import { FilterSearch } from '@/components/common/filters/FilterSearch'
import { Button } from '@/components/shadcn/button'
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue,
} from '@/components/shadcn/select'
import { OrderType } from '@/api/model/orderType'
import {
  useDeleteApiAdminFeatureFlagsFeatureFlagId,
  useGetApiAdminFeatureFlags,
  getGetApiAdminFeatureFlagsQueryKey,
} from '@/api/generated/administrator-user/administrator-user'
import {type FeatureFlagResponse, FeatureFlagScope} from '@/api/model'
import type { TFunction } from 'i18next'
import { notify } from '@/lib/notify'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'

const DEFAULT_PAGE_SIZE = 20

export function FeatureFlagsTable() {
  const { t } = useTranslation(['super-admin-feature-flags', 'common'])
  const queryClient = useQueryClient()
  const [confirmFlag, setConfirmFlag] = useState<FeatureFlagResponse | null>(null)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [search, setSearch] = useState<string | undefined>()
  const [sort, setSort] = useState<string | undefined>('featureName')
  const [order, setOrder] = useState<typeof OrderType[keyof typeof OrderType] | undefined>(OrderType.Ascending)
  const [scope, setScope] = useState<typeof FeatureFlagScope[keyof typeof FeatureFlagScope] | undefined>()

  const { mutateAsync: deleteFlag, isPending: isDeleting } = useDeleteApiAdminFeatureFlagsFeatureFlagId()

  function handleSort(field: string | undefined, ord: typeof OrderType[keyof typeof OrderType] | undefined) {
    setSort(field)
    setOrder(ord)
    setPage(1)
  }

  const { data, isLoading, isError } = useGetApiAdminFeatureFlags({
    Offset: (page - 1) * pageSize,
    Limit: pageSize,
    Sort: sort,
    Order: order,
    Search: search,
    Scope: scope,
  })

  const items = data?.data?.items ?? []
  const totalCount = data?.data?.totalCount ?? 0

  async function handleDelete(id: number) {
    try {
      await deleteFlag({ featureFlagId: id })
      queryClient.invalidateQueries({ queryKey: getGetApiAdminFeatureFlagsQueryKey() })
      notify.success(t('toasts.deleted'))
      setConfirmFlag(null)
    } catch (error) {
      notify.apiError(error, t('toasts.deleteError'))
    }
  }

  return (
    <>
      <FilterRow>
        <FilterSearch
          searchLabel={t('filters.search')}
          search={search}
          onChange={(value) => { setSearch(value || undefined); setPage(1) }}
        />
        <Select
          value={scope ?? '__all__'}
          onValueChange={(val) => {
            setScope(val === '__all__' ? undefined : val as typeof FeatureFlagScope[keyof typeof FeatureFlagScope])
            setPage(1)
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">{t('filters.scope.all')}</SelectItem>
            <SelectItem value={FeatureFlagScope.Global}>{t('dialog.scope.global')}</SelectItem>
            <SelectItem value={FeatureFlagScope.Company}>{t('dialog.scope.company')}</SelectItem>
            <SelectItem value={FeatureFlagScope.User}>{t('dialog.scope.user')}</SelectItem>

          </SelectContent>
        </Select>
      </FilterRow>

      <TableCard>
        <div className="flex-1 overflow-auto scrollbar-custom relative">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-card shadow-sm [&_th]:bg-card">
              <TableRow>
                <SortableTableHead field="featureName" currentSort={sort} currentOrder={order} onSort={handleSort}>
                  {t('table.columns.featureName')}
                </SortableTableHead>
                <TableHead>{t('table.columns.scope')}</TableHead>
                <SortableTableHead field="companyName" currentSort={sort} currentOrder={order} onSort={handleSort}>
                  {t('table.columns.target')}
                </SortableTableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              <LoaderTable isLoading={isLoading} isError={isError} isEmpty={items.length === 0} colSpan={4} emptyMessage={t('table.empty')}>
                {items.map((flag) => (
                  <TableRow key={flag.id}>
                    <TableCell>{flag.featureName}</TableCell>
                    <TableCell>{resolveScope(flag, t)}</TableCell>
                    <TableCell>{resolveTarget(flag)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setConfirmFlag(flag)}
                      >
                        {t('actions.delete')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </LoaderTable>
            </TableBody>
          </Table>
        </div>
        <PaginationFooter
          offset={(page - 1) * pageSize}
          limit={pageSize}
          totalCount={totalCount}
          onOffsetChange={(offset) => setPage(Math.floor(offset / pageSize) + 1)}
          onPageSizeChange={(size) => { setPageSize(size); setPage(1) }}
        />
      </TableCard>

      <ConfirmDialog
        open={!!confirmFlag}
        title={t('delete.title')}
        description={t('delete.description', {
          name: confirmFlag?.featureName ?? '',
          target: confirmFlag ? resolveScopeTarget(confirmFlag, t) : '',
        })}
        confirmLabel={t('buttons.delete', { ns: 'common' })}
        confirmButtonVariant="destructive"
        isLoading={isDeleting}
        onConfirm={() => confirmFlag?.id != null && handleDelete(confirmFlag.id)}
        onClose={() => setConfirmFlag(null)}
      />
    </>
  )
}

function resolveScopeTarget(flag: FeatureFlagResponse, t: TFunction): string {
  const target = resolveTarget(flag)
  return flag.userId || flag.companyId ? `${resolveScope(flag, t)}: ${target}` : resolveScope(flag, t)
}

function resolveScope(flag: FeatureFlagResponse, t: TFunction): string {
  if (flag.userId) return t('dialog.scope.user')
  if (flag.companyId) return t('dialog.scope.company')
  return t('dialog.scope.global')
}

function resolveTarget(flag: FeatureFlagResponse): string {
  if (flag.userId) return flag.userName ?? flag.userId
  if (flag.companyId) return flag.companyName ?? String(flag.companyId)
  return '—'
}
